import { ActionIcon, Checkbox, Flex, Group, Pagination, Table } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelectableTableCell } from '../../../../../../shared/hook/useSelectableTableCell';
import type { IModem } from '../../../../../../shared/types/modem';
import { cleanParams } from '../../../../../../shared/utils/cleanParams';
import { handleChangeLocaleTime } from '../../../../../../shared/utils/time';
import { renderActivateStatus } from '../../common/utils/format';

interface IPropsModemTable {
  modemList: IModem[];
  checkBoxArr: number[];
  isAllChecked: boolean;
  onClickCheckBox: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClickCheckBoxAll: (e: React.ChangeEvent<HTMLInputElement>) => void;
  totalPage: number;
}

function getContractPeriodRange(activateStartDt: string, contractPeriod: number) {
  if (!activateStartDt || !contractPeriod) return '-';

  const startDate = handleChangeLocaleTime(activateStartDt, { includeTime: false });

  if (!startDate) return '-';

  const endDate = dayjs(activateStartDt).add(contractPeriod, 'month').format('YYYY-MM-DD');

  return `${startDate} ~ ${endDate}`;
}

export default function ModemTable(props: IPropsModemTable) {
  const { modemList, checkBoxArr, isAllChecked, onClickCheckBox, onClickCheckBoxAll, totalPage } = props;
  const { tableWrapperRef, handleSelectCell, handleKeyDown, getSelectableCellStyle } = useSelectableTableCell<number>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pg = searchParams.get('pg') || '1';

  return (
    <>
      <Table.ScrollContainer
        minWidth={1200}
        ref={tableWrapperRef}
        tabIndex={0}
        onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => void handleKeyDown(e)}
        style={{ outline: 'none' }}
      >
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <Checkbox checked={isAllChecked} onChange={(e) => onClickCheckBoxAll(e)} />
              </Table.Th>
              <Table.Th>번호</Table.Th>
              <Table.Th>모뎀/라우터</Table.Th>
              <Table.Th>단말기 일련번호</Table.Th>
              <Table.Th>USIM 일련번호</Table.Th>
              <Table.Th>연결된 MAC번호</Table.Th>
              <Table.Th>개통 진행 상태</Table.Th>
              <Table.Th>개통일</Table.Th>
              <Table.Th>약정 기간</Table.Th>
              <Table.Th>등록일자</Table.Th>
              <Table.Th>상세보기</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {modemList.length > 0 ? (
              modemList.map((item) => {
                const activateStatusText = renderActivateStatus(item.activateStatus);
                const activateStartDateText = handleChangeLocaleTime(item.activateStartDt) || '-';
                const contractPeriodRange = getContractPeriodRange(item.activateStartDt, item.contractPeriod);
                const regDateText = handleChangeLocaleTime(item.regDt) || '-';
                return (
                  <Table.Tr key={item.id}>
                    <Table.Td>
                      <Checkbox
                        id={String(item.id)}
                        disabled={!!item.mac}
                        checked={checkBoxArr.includes(item.id)}
                        onChange={(e) => onClickCheckBox(e)}
                      />
                    </Table.Td>
                    <Table.Td
                      style={getSelectableCellStyle(item.id, 'rowNumber')}
                      onClick={() => handleSelectCell(item.id, 'rowNumber', String(item.id))}
                    >
                      {item.id}
                    </Table.Td>
                    <Table.Td
                      style={getSelectableCellStyle(item.id, 'modem')}
                      onClick={() => handleSelectCell(item.id, 'modem', item.modem)}
                    >
                      {item.modem}
                    </Table.Td>
                    <Table.Td
                      style={getSelectableCellStyle(item.id, 'modemNo')}
                      onClick={() => handleSelectCell(item.id, 'modemNo', item.modemNo)}
                    >
                      {item.modemNo}
                    </Table.Td>
                    <Table.Td
                      style={getSelectableCellStyle(item.id, 'usimNo')}
                      onClick={() => handleSelectCell(item.id, 'usimNo', item.usimNo)}
                    >
                      {item.usimNo}
                    </Table.Td>
                    <Table.Td
                      style={getSelectableCellStyle(item.id, 'mac')}
                      onClick={() => handleSelectCell(item.id, 'mac', item.mac)}
                    >
                      {item.mac}
                    </Table.Td>
                    <Table.Td
                      style={getSelectableCellStyle(item.id, 'activateStatus')}
                      onClick={() => handleSelectCell(item.id, 'activateStatus', activateStatusText)}
                    >
                      {activateStatusText}
                    </Table.Td>
                    <Table.Td
                      style={getSelectableCellStyle(item.id, 'activateStartDt')}
                      onClick={() => handleSelectCell(item.id, 'activateStartDt', activateStartDateText)}
                    >
                      {activateStartDateText}
                    </Table.Td>
                    <Table.Td
                      style={getSelectableCellStyle(item.id, 'contractPeriod')}
                      onClick={() => handleSelectCell(item.id, 'contractPeriod', contractPeriodRange)}
                    >
                      {contractPeriodRange}
                    </Table.Td>
                    <Table.Td
                      style={getSelectableCellStyle(item.id, 'regDt')}
                      onClick={() => handleSelectCell(item.id, 'regDt', regDateText)}
                    >
                      {regDateText}
                    </Table.Td>
                    <Table.Td>
                      <Flex justify="center" align="center" height="100%">
                        <ActionIcon
                          size="xs"
                          variant="transparent"
                          onClick={() => navigate(`/admin/management/modem/details/${item.id}`)}
                        >
                          <IconSearch size={16} />
                        </ActionIcon>
                      </Flex>
                    </Table.Td>
                  </Table.Tr>
                );
              })
            ) : (
              <Table.Tr>
                <Table.Td colSpan={11}>
                  <Flex justify="center" align="center" height={200}>
                    데이터가 없습니다.
                  </Flex>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Group justify="center" mt="md">
        <Pagination
          value={Number(pg)}
          total={totalPage}
          onChange={(value: number) => {
            const currentParams = Object.fromEntries(searchParams.entries());
            setSearchParams(
              cleanParams({
                ...currentParams,
                pg: String(value),
              }),
            );
          }}
        />
      </Group>
    </>
  );
}
